/**
 * Areas of Focus field behaviour.
 *
 * - Checking a parent option expands its "Specific Areas" sub-list and checks
 *   every child; unchecking collapses the sub-list and unchecks the children.
 *   Unchecking an individual child never affects the parent.
 * - The "Other (specify)" checkbox toggles its free-text rows; the add button
 *   clones the row template.
 * - Each group has a "Select all / Unselect all" pair for its top-level
 *   options. "Select all" skips "Other (specify)" — there's nothing to select
 *   there, only free text to type — but "Unselect all" clears it. Each
 *   specific-areas box has its own pair scoped to just its children.
 */

const dispatchChange = (input: HTMLInputElement): void => {
  input.dispatchEvent(new Event("change", { bubbles: true }));
};

const attachExpandable = (option: Element): void => {
  const parent = option.querySelector<HTMLInputElement>(".js-aof-parent");
  const specific = option.querySelector<HTMLElement>(".js-aof-specific");
  if (!parent || !specific) {
    return;
  }

  parent.addEventListener("change", () => {
    specific.classList.toggle("hidden", !parent.checked);
    parent.setAttribute("aria-expanded", String(parent.checked));

    specific
      .querySelectorAll<HTMLInputElement>(".js-aof-child")
      .forEach((child) => {
        if (child.checked !== parent.checked) {
          child.checked = parent.checked;
          dispatchChange(child);
        }
      });
  });

  const specificSelectAll = specific.querySelector<HTMLElement>(
    ".js-aof-specific-select-all"
  );
  const specificUnselectAll = specific.querySelector<HTMLElement>(
    ".js-aof-specific-unselect-all"
  );
  if (specificSelectAll && specificUnselectAll) {
    const setChildren = (checked: boolean): void => {
      specific
        .querySelectorAll<HTMLInputElement>(".js-aof-child")
        .forEach((child) => {
          if (child.checked !== checked) {
            child.checked = checked;
            dispatchChange(child);
          }
        });
    };

    specificSelectAll.addEventListener("click", () => setChildren(true));
    specificUnselectAll.addEventListener("click", () => setChildren(false));
  }
};

const attachOther = (option: Element): void => {
  const toggle = option.querySelector<HTMLInputElement>(".js-aof-other-toggle");
  const wrapper = option.querySelector<HTMLElement>(".areas-of-focus__other");
  const rows = option.querySelector<HTMLElement>(".js-aof-other-rows");
  const template = option.querySelector<HTMLTemplateElement>(
    ".js-aof-other-template"
  );
  const addButton = option.querySelector<HTMLElement>(".js-aof-other-add");
  if (!toggle || !wrapper || !rows || !template || !addButton) {
    return;
  }

  toggle.addEventListener("change", () => {
    wrapper.classList.toggle("hidden", !toggle.checked);
    toggle.setAttribute("aria-expanded", String(toggle.checked));

    if (!toggle.checked) {
      // Unchecking "Other" clears the entries so they aren't submitted.
      rows
        .querySelectorAll<HTMLInputElement>("input")
        .forEach((input) => (input.value = ""));
    } else if (!rows.querySelector(".areas-of-focus__other-row")) {
      // Re-checking "Other" with no rows left from a prior delete-all should
      // still leave one to type into. Reached by hand only: deleting the last
      // row unchecks the toggle, so the next click arrives with zero rows.
      rows.append(template.content.cloneNode(true));
    }
  });

  addButton.addEventListener("click", () => {
    rows.append(template.content.cloneNode(true));
    rows
      .querySelector<HTMLInputElement>(
        ".areas-of-focus__other-row:last-child input"
      )
      ?.focus();
  });

  // Removing the last row is equivalent to unchecking "Other" — it clears
  // the wrapper and collapses, rather than leaving an empty, input-less box.
  rows.addEventListener("click", (event) => {
    const removeButton = (event.target as HTMLElement).closest(
      ".js-aof-other-remove"
    );
    if (!removeButton) {
      return;
    }

    removeButton.closest(".areas-of-focus__other-row")?.remove();

    if (!rows.querySelector(".areas-of-focus__other-row")) {
      toggle.checked = false;
      dispatchChange(toggle);
    }
  });
};

const attachGroupSelectAll = (group: Element): void => {
  const selectAll = group.querySelector<HTMLElement>(
    ".js-aof-group-select-all"
  );
  const unselectAll = group.querySelector<HTMLElement>(
    ".js-aof-group-unselect-all"
  );
  if (!selectAll || !unselectAll) {
    return;
  }

  // Scoped to top-level (L1) checkboxes only — specific-areas (L2) children
  // are nested one level deeper, inside .areas-of-focus__specific.
  const setAll = (checked: boolean): void => {
    Array.from(group.querySelectorAll<HTMLInputElement>(".js-field-choice"))
      .filter((choice) => !choice.closest(".areas-of-focus__specific"))
      // "Select all" means every real area of focus. "Other (specify)" holds
      // nothing to select — only free text to type — so checking it on the
      // member's behalf asserts something they never said. "Unselect all" does
      // still reach it, because unchecking clears the rows it holds and so is a
      // genuine reset.
      .filter((choice) => !(checked && choice.closest(".js-aof-other")))
      .forEach((choice) => {
        if (choice.checked !== checked) {
          choice.checked = checked;
          dispatchChange(choice);
        }
      });
  };

  selectAll.addEventListener("click", () => setAll(true));
  unselectAll.addEventListener("click", () => setAll(false));
};

const initialize = (): void =>
  document.querySelectorAll(".js-areas-of-focus").forEach((field) => {
    if ((field as HTMLElement).dataset.initialized) {
      return;
    }
    (field as HTMLElement).dataset.initialized = "true";

    field.querySelectorAll(".js-aof-expandable").forEach(attachExpandable);
    field.querySelectorAll(".js-aof-other").forEach(attachOther);
    field
      .querySelectorAll(".areas-of-focus__group")
      .forEach(attachGroupSelectAll);
  });

initialize();

window.addEventListener("htmx:afterSwap", initialize);

export default initialize;
